import { __assign, __makeTemplateObject, __rest } from "tslib";
import React, { forwardRef, useContext, useImperativeHandle, useMemo, } from 'react';
import clsx from 'clsx';
import MuiMenuItem from '@material-ui/core/MenuItem';
import { Check as checkSvg } from '@ringcentral/juno-icon';
import { combineClasses, isRcElement, logInDev, omit, styled, useEventCallback, useId, useThemeProps, combineProps, } from '../../../foundation';
import { RcIcon } from '../../Icon';
import { RcListItemSecondaryAction } from '../../List';
import { RcListItemAvatar } from '../../List/ListItemAvatar';
import { RcListItemIcon } from '../../List/ListItemIcon';
import { withTooltip } from '../../Tooltip';
import { RcMenuContext } from '../Menu/MenuContext';
import { RcMenuListContext } from '../MenuList/MenuListContext';
import { RcSubMenuContext } from '../SubMenu/SubMenuContext';
import { MenuItemStyle, StyledCheckIcon } from './styles';
import { RcMenuItemClasses, RcMenuItemRippleClasses } from './utils';
var _RcMenuItem = forwardRef(function (props, ref) {
    var _a;
    var classesProp = props.classes, children = props.children, color = props.color, button = props.button, className = props.className, onMouseEnter = props.onMouseEnter, onClick = props.onClick, onFocus = props.onFocus, highlighted = props.highlighted, size = props.size, type = props.type, checked = props.checked, icon = props.icon, symbol = props.symbol, avatar = props.avatar, secondaryAction = props.secondaryAction, idRef = props.idRef, isSubMenuItem = props.isSubMenuItem, TouchRipplePropsProp = props.TouchRippleProps, title = props.title, focused = props.focused, focusVariant = props.focusVariant, rest = __rest(props, ["classes", "children", "color", "button", "className", "onMouseEnter", "onClick", "onFocus", "highlighted", "size", "type", "checked", "icon", "symbol", "avatar", "secondaryAction", "idRef", "isSubMenuItem", "TouchRippleProps", "title", "focused", "focusVariant"]);
    var isCheckedType = type === 'checked';
    var menuItemId = useId('menu-item', true);
    var menuContext = useContext(RcMenuContext);
    var menuListContext = useContext(RcMenuListContext);
    var subMenuContext = useContext(RcSubMenuContext);
    var setFocusedMenuItemId = useEventCallback(function () {
        if (!menuItemId)
            return;
        if (menuListContext.menuListId) {
            menuListContext.setFocusedMenuItemId(menuItemId);
        }
        else if (menuContext.menuId) {
            menuContext.setFocusedMenuItemId(menuItemId);
        }
    });
    var classes = useMemo(function () { return combineClasses(RcMenuItemClasses, classesProp); }, [classesProp]);
    var toClasses = useMemo(function () { return omit(classes, ['checked', 'unchecked', 'focusVisible']); }, [classes]);
    var itemAvatar = useMemo(function () {
        if (React.isValidElement(avatar)) {
            return isRcElement(avatar, ['RcListItemAvatar']) ? (avatar) : (React.createElement(RcListItemAvatar, null, avatar));
        }
        return null;
    }, [avatar]);
    var itemIcon = useMemo(function () {
        if (symbol) {
            return (React.createElement(RcListItemIcon, null,
                React.createElement(RcIcon, { size: "small", symbol: symbol })));
        }
        if (React.isValidElement(icon)) {
            return isRcElement(icon, ['RcListItemIcon']) ? (icon) : (React.createElement(RcListItemIcon, null, icon));
        }
        if (typeof icon === 'string') {
            if (process.env.NODE_ENV !== 'production') {
                logInDev({
                    component: 'RcMenuItem',
                    message: 'please use symbol to replace icon string',
                });
            }
            return (React.createElement(RcListItemIcon, null,
                React.createElement(RcIcon, { size: "small" }, icon)));
        }
        return null;
    }, [icon, symbol]);
    var itemSubAction = useMemo(function () {
        if (isCheckedType && checked) {
            return (React.createElement(RcListItemSecondaryAction, null,
                React.createElement(StyledCheckIcon, { size: "large", symbol: checkSvg, color: ['interactive', 'f01'] })));
        }
        return secondaryAction ? (isRcElement(secondaryAction, ['RcListItemSecondaryAction']) ? (secondaryAction) : (React.createElement(RcListItemSecondaryAction, null, secondaryAction))) : null;
    }, [checked, isCheckedType, secondaryAction]);
    // * when not button can't have TouchRippleProps
    var additionProps = useMemo(function () {
        return button
            ? {
                TouchRippleProps: combineProps({ classes: RcMenuItemRippleClasses }, TouchRipplePropsProp),
            }
            : {};
    }, [TouchRipplePropsProp, button]);
    var handleMouseEnter = useEventCallback(function (e) {
        onMouseEnter === null || onMouseEnter === void 0 ? void 0 : onMouseEnter(e);
        setFocusedMenuItemId();
    });
    var handleClick = useEventCallback(function (e) {
        var _a;
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
        if (menuContext.autoClose && !isSubMenuItem) {
            menuContext.closeMenu(e, 'itemClick');
        }
        if (subMenuContext.autoClose && !isSubMenuItem) {
            (_a = subMenuContext === null || subMenuContext === void 0 ? void 0 : subMenuContext.closeSubMenu) === null || _a === void 0 ? void 0 : _a.call(subMenuContext, e, 'itemClick');
        }
    });
    var handleFocus = useEventCallback(function (e) {
        setFocusedMenuItemId();
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    });
    useImperativeHandle(idRef, function () { return menuItemId; }, [menuItemId]);
    return (React.createElement(MuiMenuItem, __assign({ "aria-checked": isCheckedType && checked ? true : undefined }, additionProps, rest, { ref: ref, title: typeof title === 'string' ? title : undefined, classes: toClasses, className: clsx(className, (_a = {},
            _a[classes.checked] = isCheckedType && checked,
            _a[classes.unchecked] = isCheckedType && !checked,
            _a[classes.focusVisible] = focused,
            _a)), onMouseEnter: handleMouseEnter, onClick: handleClick, onFocus: handleFocus, "data-menuitem-id": menuItemId, button: button }),
        itemAvatar,
        itemIcon,
        children,
        itemSubAction));
});
var RcMenuItem = styled(withTooltip(_RcMenuItem)).attrs(function (inProps) {
    var _a = useThemeProps({ props: inProps, name: 'RcMenuItem' }), 
    // @ts-ignore
    theme = _a.theme, 
    // Omit className, prevent duplicate className
    className = _a.className, _b = _a.size, size = _b === void 0 ? 'medium' : _b, _c = _a.button, button = _c === void 0 ? true : _c, _d = _a.focusVariant, focusVariant = _d === void 0 ? 'highlight' : _d, rest = __rest(_a, ["theme", "className", "size", "button", "focusVariant"]);
    return __assign({ size: size,
        button: button,
        focusVariant: focusVariant }, rest);
})(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", ";\n"], ["\n  ", ";\n"])), MenuItemStyle);
RcMenuItem.displayName = 'RcMenuItem';
export { RcMenuItem };
var templateObject_1;
